import React from 'react';
import { Form } from 'react-bootstrap';
import { TFunc } from '../../../../../../../hooks/useNamespacedTranslation';
import {
  FILE_SIZE_LIMIT,
  collectFiles,
  fileSizeFormatter,
} from '../../../../../../../utils/file';
import { useToasts } from '../../../../../../../context/Toast';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import classNames from 'classnames';

export type FileData = {
  fileName: string;
  fileBase64: string;
  size: number;
};

type Props = {
  t: TFunc;
  onFilesChange: (files: FileData[]) => void;
  files: FileData[];
};

export const FileUploadFormField: React.FC<React.PropsWithChildren<Props>> = ({
  t,
  onFilesChange,
  files,
}) => {
  const { addError } = useToasts();

  const onFileChange = async ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.validity.valid && target.files) {
      const newFiles = await collectFiles(target.files);

      const oversizedFile = newFiles.find(
        (file) => file.size > FILE_SIZE_LIMIT,
      );

      if (oversizedFile) {
        addError(
          t('form.fields.fileSizeLimitError', {
            size: fileSizeFormatter(FILE_SIZE_LIMIT),
          }),
        );
        target.value = '';
      } else {
        onFilesChange(newFiles);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    onFilesChange(updatedFiles);
  };

  return (
    <div>
      <Form.Group>
        <Form.Label>
          {t('form.fields.fileLabel')}
          <span className="text-muted ms-2 small">
            ({t('form.fields.fileOptional')})
          </span>
        </Form.Label>
        <div className="small text-muted mb-2">
          {t('form.fields.fileSizeHint', {
            size: fileSizeFormatter(FILE_SIZE_LIMIT),
          })}
        </div>
        {files.length === 0 && (
          <Form.Control
            id="ticketFile"
            type="file"
            multiple
            onChange={onFileChange}
          />
        )}
      </Form.Group>

      {files.length > 0 && (
        <div className="mt-3">
          {files.map((file, index) => (
            <div
              key={index}
              className={classNames(
                'd-flex',
                'align-items-center',
                'justify-content-between',
                'py-2',
                'gap-3',
              )}>
              <div className="d-flex align-items-center gap-2">
                <FlairIcon
                  icon="document-outline"
                  size="sm"
                  className="text-muted"
                />
                <span>{file.fileName}</span>
                <span className="text-muted small">
                  ({fileSizeFormatter(file.size)})
                </span>
              </div>
              <FlairIcon
                icon="trash-outline"
                className="text-dark align-middle"
                role="button"
                onClick={() => handleRemoveFile(index)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
